/**
 * @description: userInfo 和 permissionList 本地存储工具，支持多站点隔离
 * 不同站点使用不同的 localStorage key，避免登录信息互相覆盖
 *
 * 使用方式:
 * import { getUserInfo, setUserInfo, getPermissionList, setPermissionList } from '@/utils/userInfoStorage'
 * // 替换: JSON.parse(localStorage.getItem('userInfo')) || {}
 * // 替换: localStorage.setItem('userInfo', JSON.stringify(data))
 */

const SITE_KEY_MAP = {
  en: {
    userInfo: 'userInfo_en',
    permissionList: 'permissionList_en'
  },
  eu: {
    userInfo: 'userInfo_eu',
    permissionList: 'permissionList_eu'
  }
}

const OLD_KEYS = {
  userInfo: 'userInfo',
  permissionList: 'permissionList'
}

const getSiteConfig = () => {
  const site = process.env.VUE_APP_BASE_SITE
  return SITE_KEY_MAP[site] || null // null 表示使用旧 key
}

const getKey = (type) => {
  const config = getSiteConfig()
  if (config) {
    return config[type]
  }
  return OLD_KEYS[type]
}

/**
 * 获取 userInfo
 * 优先级: 新key (site-specific) > 旧key (向后兼容)
 */
export const getUserInfo = () => {
  try {
    const siteKey = getKey('userInfo')
    const config = getSiteConfig()
    // 1. 先尝试站点专用 key
    if (config) {
      const siteData = localStorage.getItem(siteKey)
      if (siteData) {
        return JSON.parse(siteData)
      }
    }
    // 2. 兼容旧 key (首次登录或未迁移的用户)
    const oldData = localStorage.getItem(OLD_KEYS.userInfo)
    if (oldData) {
      // 尝试迁移到新 key
      if (config && siteKey) {
        try {
          localStorage.setItem(siteKey, oldData)
        } catch (e) {
          // 忽略存储配额错误
        }
      }
      return JSON.parse(oldData)
    }
    return null
  } catch (e) {
    console.error('getUserInfo error:', e)
    return null
  }
}

/**
 * 设置 userInfo
 */
export const setUserInfo = (userInfo) => {
  try {
    const key = getKey('userInfo')
    localStorage.setItem(key, JSON.stringify(userInfo || {}))
  } catch (e) {
    console.error('setUserInfo error:', e)
  }
}

/**
 * 获取 permissionList
 */
export const getPermissionList = () => {
  try {
    const siteKey = getKey('permissionList')
    const config = getSiteConfig()
    // 1. 先尝试站点专用 key
    if (config) {
      const siteData = localStorage.getItem(siteKey)
      if (siteData) {
        return JSON.parse(siteData)
      }
    }
    // 2. 兼容旧 key
    const oldData = localStorage.getItem(OLD_KEYS.permissionList)
    if (oldData) {
      if (config && siteKey) {
        try {
          localStorage.setItem(siteKey, oldData)
        } catch (e) {
          // 忽略存储配额错误
        }
      }
      return JSON.parse(oldData)
    }
    return null
  } catch (e) {
    console.error('getPermissionList error:', e)
    return null
  }
}

/**
 * 设置 permissionList
 */
export const setPermissionList = (permissionList) => {
  try {
    const key = getKey('permissionList')
    localStorage.setItem(key, JSON.stringify(permissionList || []))
  } catch (e) {
    console.error('setPermissionList error:', e)
  }
}

/**
 * 移除 userInfo 和 permissionList
 */
export const removeUserInfo = () => {
  const config = getSiteConfig()
  if (config) {
    localStorage.removeItem(config.userInfo)
    localStorage.removeItem(config.permissionList)
  }
  localStorage.removeItem(OLD_KEYS.userInfo)
  localStorage.removeItem(OLD_KEYS.permissionList)
}

/**
 * 兼容旧方法 - 推荐使用 getUserInfo/setUserInfo
 */
export const getUserInfoFromStorage = () => {
  return getUserInfo()
}

export default {
  get: getUserInfo,
  set: setUserInfo,
  getPermissionList,
  setPermissionList,
  remove: removeUserInfo,
  getSiteKey: getKey
}